import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

interface AccuracyGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
}

function AccuracyGuidelinesModal({ open, onClose }: AccuracyGuidelinesModalProps) {
  const handlePrint = () => {
    const printContent = document.getElementById('accuracy-guidelines-content');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Clone the content and clean up Material-UI specific classes for better printing
    const clonedContent = printContent.cloneNode(true) as HTMLElement;
    
    // Remove Material-UI specific attributes that might interfere with printing
    const allElements = clonedContent.querySelectorAll('*');
    allElements.forEach((el) => {
      el.removeAttribute('class');
      el.removeAttribute('data-testid');
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Guidelines for the use and interpretation of the accuracy report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              line-height: 1.6;
              color: #333;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #063142;
              margin-top: 20px;
              margin-bottom: 12px;
            }
            p {
              margin-bottom: 12px;
            }
            ol {
              margin-left: 20px;
              margin-bottom: 12px;
              padding-left: 20px;
            }
            li {
              margin-bottom: 12px;
              line-height: 1.6;
            }
            span {
              font-weight: inherit;
            }
            @media print {
              body {
                padding: 20px;
              }
              @page {
                margin: 1cm;
              }
            }
          </style>
        </head>
        <body>
          <h1>Guidelines for the use and interpretation of the accuracy report</h1>
          ${clonedContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E1E8ED',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142' }}>
          Guidelines for the use and interpretation of the accuracy report
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 3,
          maxHeight: 'calc(90vh - 140px)',
          overflowY: 'auto',
        }}
      >
        <Box id="accuracy-guidelines-content">
          <Typography variant="body1" sx={{ mb: 3, color: '#063142' }}>
            Interpretation of the accuracy report:
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            For purposes of clarity, use the less detailed view (hide details). For the definition of the terms "accuracy", "precision", "bias" etc. see the sampling chapter of the REIMS Operational Guide.
          </Typography>

          <Box component="ol" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                Check the number of sampled receptacles per stratum:
              </Typography>
              {' '}For reliable estimates, sample sizes for each stratum should be larger than about 30. In case of smaller sample sizes and sampling rates higher than 1 out of 3, the results should be investigated (insufficient sample sizes are highlighted in red). The target sample size of 30 is a widely accepted rule of thumb, but not a strict limit. Minor deviations are therefore tolerable.
            </Typography>

            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                Compare the accuracies per format and service type with the target accuracies:
              </Typography>
              {' '}given in the corresponding row. If the accuracy target is not achieved (i.e. the percentage value of the accuracy is higher than the corresponding target accuracy and therefore highlighted in red), then the results should be investigated. However, as accuracy is an estimate, too, a minor exceeding of the target should be tolerated.
            </Typography>

            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                Accuracies for single strata:
              </Typography>
              {' '}are generally worse than the target accuracies per format and service. As there are no accuracy targets for single strata they do not constitute a claim.
            </Typography>

            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                Values highlighted in red:
              </Typography>
              {' '}indicate potential deficiencies of the data. However, the occurrence of "red" figures does not imply that the underlying data are completely wrong or inacceptable. "Red" figures mean that the general sampling principles as described in the REIMS Operational Guide chapter 3.1ff are violated in some way. You should first investigate possible reasons for the deficiency involving also the other PPO. Please refer to the Operational Guide on how to solve a case where data is disputed.
            </Typography>

            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                Absence of "red" figures:
              </Typography>
              {' '}does not imply that the underlying data are perfect and must be accepted without amendments.
            </Typography>

            <Typography component="li" variant="body2" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                  6.1. The accuracy figures can be used:
                </Typography>
                {' '}in order to adjust sampling rates for the next quarter(s). E.g. if the accuracy is worse than the target accuracy the corresponding sample rates should be reduced. On the other hand very good accuracies could justify an increase of the corresponding sampling rates.
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#063142' }}>
                  6.2. For further analyses:
                </Typography>
                {' '}and the calculation of sampling rates use the additional variables provided on the detailed view. They are explained in the eNO1 Accuracy Guide. The entire table can be downloaded as an Excel file for further processing. Annex 3F of the Operational Guide describes in detail how sample sizes and thus sampling rates can be computed from the variables of the detailed view of the Accuracy Report.
              </Box>
            </Typography>
          </Box>

          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #E1E8ED' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
              REIMS Sampling Subgroup
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
              14th April 2010
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #E1E8ED', justifyContent: 'space-between' }}>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          variant="outlined"
          sx={{
            color: '#063142',
            borderColor: '#D1D9E0',
            '&:hover': {
              borderColor: '#00ACE7',
              backgroundColor: '#F8F9FA',
            },
          }}
        >
          Print
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AccuracyGuidelinesModal;

